import { createReducer } from '@reduxjs/toolkit';

import type { ChatMessage, ConversationSummary } from '../../services/chatApi';
import { logout } from '../auth/authActions';
import {
	chatClosed,
	chatConversationsFailure,
	chatConversationsRequest,
	chatConversationsSuccess,
	chatMarkReadRequest,
	chatMessageReceived,
	chatMessagesFailure,
	chatMessagesRequest,
	chatMessagesSuccess,
	chatOpened,
	chatResolveFailure,
	chatResolveRequest,
	chatResolveSuccess,
	chatSendFailure,
	chatSendRequest,
	chatSendSuccess,
	chatUnlocked,
	chatUnreadCountSuccess,
} from './chatActions';

/**
 * A message as the thread shows it. A send of ours still in flight, or one that failed, carries
 * its client id, its status and, once failed, why.
 */
export type ChatMessageView = ChatMessage & {
	clientId?: string;
	status?: 'sending' | 'failed';
	error?: string;
	/** A failed send that may succeed if tried again; one the chat refused may not. */
	retryable?: boolean;
};

export type ChatThread = {
	/** Oldest first. */
	items: ChatMessageView[];
	hasMore: boolean;
	/** The newest page has arrived at least once. */
	loaded: boolean;
	loading: boolean;
	loadingOlder: boolean;
	errorKey?: string;
};

export type ChatResolution = {
	conversationId: string | null;
	unlocked: boolean;
	loading: boolean;
	errorKey?: string;
};

export type ChatState = {
	conversations: {
		items: ConversationSummary[];
		loaded: boolean;
		loading: boolean;
		errorKey?: string;
	};
	unreadTotal: number;
	/** Keyed by the other player's id. */
	byPlayerId: Record<string, ChatResolution>;
	/** Keyed by conversation id. */
	threads: Record<string, ChatThread>;
	openConversationId: string | null;
};

const initial: ChatState = {
	conversations: { items: [], loaded: false, loading: false },
	unreadTotal: 0,
	byPlayerId: {},
	threads: {},
	openConversationId: null,
};

const emptyThread = (): ChatThread => ({
	items: [],
	hasMore: false,
	loaded: false,
	loading: false,
	loadingOlder: false,
});

const threadOf = (s: ChatState, conversationId: string) => {
	if (!s.threads[conversationId]) s.threads[conversationId] = emptyThread();
	return s.threads[conversationId];
};

/** Delivered messages go in before our own bubbles still in flight, which stay last. */
const appendDelivered = (thread: ChatThread, message: ChatMessage) => {
	const firstPending = thread.items.findIndex(m => m.status);
	if (firstPending < 0) thread.items.push(message);
	else thread.items.splice(firstPending, 0, message);
};

/** Puts `message` on its conversation's row and lifts the row to the top of the list. */
const touchConversation = (s: ChatState, message: ChatMessage) => {
	const items = s.conversations.items;
	const index = items.findIndex(c => c.id === message.conversationId);
	if (index < 0) return null;

	const conversation = items[index];
	const last = conversation.lastMessage;
	if (!last || new Date(last.createdAt).getTime() <= new Date(message.createdAt).getTime()) {
		conversation.lastMessage = message;
	}
	if (index > 0) {
		items.splice(index, 1);
		items.unshift(conversation);
	}
	return conversation;
};

export const chatReducer = createReducer(initial, b => {
	b.addCase(chatConversationsRequest, s => {
		s.conversations.loading = true;
		s.conversations.errorKey = undefined;
	});
	b.addCase(chatConversationsSuccess, (s, a) => {
		s.conversations.loading = false;
		s.conversations.loaded = true;
		s.conversations.items = a.payload.items;
		s.unreadTotal = a.payload.items.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
		for (const conversation of a.payload.items) {
			s.byPlayerId[conversation.otherPlayer.id] = {
				conversationId: conversation.id,
				unlocked: true,
				loading: false,
			};
		}
	});
	b.addCase(chatConversationsFailure, (s, a) => {
		s.conversations.loading = false;
		s.conversations.errorKey = a.payload;
	});

	b.addCase(chatUnreadCountSuccess, (s, a) => {
		s.unreadTotal = Math.max(0, a.payload.count || 0);
	});

	b.addCase(chatResolveRequest, (s, a) => {
		const previous = s.byPlayerId[a.payload.playerId];
		s.byPlayerId[a.payload.playerId] = {
			conversationId: previous?.conversationId ?? null,
			unlocked: previous?.unlocked ?? false,
			loading: true,
		};
	});
	b.addCase(chatResolveSuccess, (s, a) => {
		const { playerId, conversationId, unlocked } = a.payload;
		s.byPlayerId[playerId] = { conversationId, unlocked, loading: false };
	});
	b.addCase(chatResolveFailure, (s, a) => {
		const previous = s.byPlayerId[a.payload.playerId];
		s.byPlayerId[a.payload.playerId] = {
			conversationId: previous?.conversationId ?? null,
			unlocked: previous?.unlocked ?? false,
			loading: false,
			errorKey: a.payload.message,
		};
	});

	b.addCase(chatMessagesRequest, (s, a) => {
		const thread = threadOf(s, a.payload.conversationId);
		thread.errorKey = undefined;
		if (a.payload.older) thread.loadingOlder = true;
		else thread.loading = true;
	});
	b.addCase(chatMessagesSuccess, (s, a) => {
		const { conversationId, items, hasMore, older } = a.payload;
		const thread = threadOf(s, conversationId);
		const known = new Set(thread.items.map(m => m.id));
		const fresh = items.filter(m => !known.has(m.id));

		if (older) {
			thread.loadingOlder = false;
			thread.items = [...fresh, ...thread.items];
			thread.hasMore = hasMore;
			return;
		}

		thread.loading = false;
		// The newest page is merged in rather than swapped in: older pages already scrolled through,
		// what the socket delivered meanwhile and our own unsent bubbles all stay. Unsent ones stay last.
		const merged = [...thread.items.filter(m => !m.status), ...fresh].sort(
			(x, y) => new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime(),
		);
		thread.items = [...merged, ...thread.items.filter(m => m.status)];
		// A reload of the newest page says nothing about pages already fetched behind it.
		if (!thread.loaded) thread.hasMore = hasMore;
		thread.loaded = true;
	});
	b.addCase(chatMessagesFailure, (s, a) => {
		const thread = threadOf(s, a.payload.conversationId);
		if (a.payload.older) thread.loadingOlder = false;
		else thread.loading = false;
		thread.errorKey = a.payload.message;
	});

	b.addCase(chatSendRequest, (s, a) => {
		const { conversationId, clientId, text, senderId } = a.payload;
		const thread = threadOf(s, conversationId);
		const existing = thread.items.find(m => m.clientId === clientId && m.status);
		if (existing) {
			existing.status = 'sending';
			existing.error = undefined;
			return;
		}
		thread.items.push({
			id: clientId,
			clientId,
			conversationId,
			senderId,
			text,
			createdAt: new Date().toISOString(),
			status: 'sending',
		});
	});
	b.addCase(chatSendSuccess, (s, a) => {
		const { conversationId, clientId, message } = a.payload;
		const thread = threadOf(s, conversationId);
		const pendingIndex = thread.items.findIndex(m => m.clientId === clientId && m.status);
		const alreadyDelivered = thread.items.some(m => m.id === message.id);
		if (pendingIndex >= 0) {
			if (alreadyDelivered) thread.items.splice(pendingIndex, 1);
			else thread.items[pendingIndex] = message;
		} else if (!alreadyDelivered) {
			appendDelivered(thread, message);
		}
		touchConversation(s, message);
	});
	b.addCase(chatSendFailure, (s, a) => {
		const thread = threadOf(s, a.payload.conversationId);
		const pending = thread.items.find(m => m.clientId === a.payload.clientId && m.status);
		if (!pending) return;
		pending.status = 'failed';
		pending.error = a.payload.message;
		pending.retryable = a.payload.retryable;
	});

	b.addCase(chatMarkReadRequest, (s, a) => {
		const conversation = s.conversations.items.find(c => c.id === a.payload.conversationId);
		if (!conversation || !conversation.unreadCount) return;
		s.unreadTotal = Math.max(0, s.unreadTotal - conversation.unreadCount);
		conversation.unreadCount = 0;
	});

	b.addCase(chatOpened, (s, a) => {
		s.openConversationId = a.payload.conversationId;
	});
	b.addCase(chatClosed, (s, a) => {
		if (s.openConversationId === a.payload.conversationId) s.openConversationId = null;
	});

	b.addCase(chatMessageReceived, (s, a) => {
		const { message, myId } = a.payload;
		const mine = message.senderId === myId;
		const thread = s.threads[message.conversationId];

		if (thread?.loaded && !thread.items.some(m => m.id === message.id)) {
			// Our own message from another session - or from this one, when the push beats the POST's
			// reply: it takes the place of the matching bubble still on its way.
			const pending = mine
				? thread.items.find(m => m.status === 'sending' && m.text === message.text)
				: undefined;
			if (pending) {
				Object.assign(pending, message, {
					clientId: undefined,
					status: undefined,
					error: undefined,
				});
			} else {
				appendDelivered(thread, message);
			}
		}

		const conversation = touchConversation(s, message);
		if (mine || s.openConversationId === message.conversationId) return;
		if (conversation) conversation.unreadCount += 1;
		s.unreadTotal += 1;
	});

	b.addCase(chatUnlocked, (s, a) => {
		const { conversationId, otherPlayer } = a.payload;
		s.byPlayerId[otherPlayer.id] = { conversationId, unlocked: true, loading: false };
		if (!s.conversations.items.some(c => c.id === conversationId)) {
			s.conversations.items.unshift({
				id: conversationId,
				otherPlayer,
				lastMessage: null,
				unreadCount: 0,
				unlockedAt: new Date().toISOString(),
			});
		}
	});

	b.addCase(logout, () => initial);
});

type WithChat = { chat: ChatState };

const NO_THREAD: ChatThread = emptyThread();

export const selectChatConversations = (state: WithChat) => state.chat.conversations;
export const selectChatUnreadTotal = (state: WithChat) => state.chat.unreadTotal;
export const selectChatOpenConversationId = (state: WithChat) => state.chat.openConversationId;
export const selectChatResolution = (state: WithChat, playerId: string) =>
	state.chat.byPlayerId[playerId];
export const selectChatThread = (state: WithChat, conversationId: string | null | undefined) =>
	(conversationId && state.chat.threads[conversationId]) || NO_THREAD;
export const selectChatConversationWithPlayer = (state: WithChat, playerId: string) =>
	state.chat.conversations.items.find(c => c.otherPlayer.id === playerId);
export const selectChatConversationById = (state: WithChat, conversationId: string) =>
	state.chat.conversations.items.find(c => c.id === conversationId);
